import { ukDate } from "../lib/dates";
import React, { useEffect, useState, useCallback } from "react";
import { MemberSelfReported } from "../components/MemberSelfReported";
import { useLocation, useNavigate } from "react-router-dom";
import { MemberCellBadge, MemberCellCard } from "../components/MemberCellBadge";
import api, { formatApiError, mediaUrl } from "../lib/api";
import { logDeletion } from "../lib/undo";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Search, Plus, Pencil, Trash2, Users, Mail, Phone, PhoneCall, Download, CalendarCheck, Upload, FileUp, KeyRound, Copy, CheckCircle2, XCircle, Clock3, Info, ShieldCheck, UserPlus, HeartHandshake, Sparkles, HeartPulse, MoreVertical, Star, MessageSquare, GraduationCap } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { SortableTh, useSort, sortRows } from "../components/SortableTh";
import { StatStrip } from "../components/StatStrip";
import PhoneActions from "../components/PhoneActions";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";

const STATUS_STYLE = {
  active: { bg: "#17A88A1A", color: "#17A88A" },
  new: { bg: "#0844381A", color: "#084438" },
  inactive: { bg: "#56626A1A", color: "#56626A" },
};

const MSG_STYLE = {
  sent: { bg: "#17A88A1A", color: "#17A88A", label: "Delivered" },
  failed: { bg: "#0B5E4E1A", color: "#0B5E4E", label: "Failed" },
  simulated: { bg: "#0844381A", color: "#084438", label: "Simulated" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-GB');
};

const DeliveryBadge = ({ lm }) => {
  if (!lm?.status) return null;
  const s = MSG_STYLE[lm.status] || MSG_STYLE.simulated;
  const Icon = lm.status === "sent" ? CheckCircle2 : lm.status === "failed" ? XCircle : Clock3;
  return (
    <span
      data-testid="delivery-status-badge"
      title={`Last ${lm.channel || "message"} (${lm.category || "message"}) — ${s.label} ${timeAgo(lm.created_at)}`}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1"
      style={{ background: s.bg, color: s.color }}
    >
      <Icon className="h-3 w-3" />{s.label}
    </span>
  );
};

const empty = {
  first_name: "", last_name: "", email: "", phone: "", gender: "", address: "", postcode: "",
  date_of_birth: "", join_date: "", status: "new", membership_type: "Member",
  follow_up_status: "none", is_convert: false, convert_date: "", notes: "", prayer_request: "",
};

export default function Members() {
  const location = useLocation();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(location.state?.filter || "all");
  const [careStage, setCareStage] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);
  const [memberships, setMemberships] = useState(null);
  const [callHistory, setCallHistory] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [newCredential, setNewCredential] = useState(null);
  const [stats, setStats] = useState(null);
  const { sortKey, sortDir, onSort, setSort } = useSort();
  const avatarRef = React.useRef();

  const [cellMap, setCellMap] = useState({});
  const load = useCallback(async () => {
    setLoading(true);
    api.get("/cell/member-groups").then(({ data }) => setCellMap(data || {})).catch(() => {});
    const { data } = await api.get("/members", { params: { status: filter, search, care_stage: careStage } });
    setMembers(data);
    setLoading(false);
    try { const { data: s } = await api.get("/members/stats"); setStats(s); } catch {}
  }, [filter, search, careStage]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setAttendance(null); setMemberships(null); setCallHistory(null); setOpen(true); };
  const openEdit = async (m) => {
    setEditing(m.id); setForm({ ...empty, ...m }); setAttendance(null); setMemberships(null); setCallHistory(null); setOpen(true);
    try { const { data } = await api.get(`/members/${m.id}/attendance`); setAttendance(data); } catch (err) { console.error("Failed to load attendance:", err); }
    try { const { data } = await api.get(`/members/${m.id}/memberships`); setMemberships(data); } catch (err) { console.error("Failed to load memberships:", err); }
    try { const { data } = await api.get(`/members/${m.id}/calls`); setCallHistory(data); } catch (err) { console.error("Failed to load calls:", err); }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const exportCsv = async () => {
    try {
      const res = await api.get("/members/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `members_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Members exported");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [sendingConsent, setSendingConsent] = useState(false);
  const sendConsentReminder = async () => {
    setSendingConsent(true);
    try {
      const { data } = await api.post("/consent-reminders/send");
      const n = data.pending ?? 0;
      if (data.status === "no_recipients") toast.error("No admin/pastor email found to send the report to.");
      else if (data.status === "simulated") toast.success(`Consent report ready (email simulated) — ${n} member(s) pending`);
      else toast.success(`Consent report emailed to admins — ${n} member(s) pending`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSendingConsent(false); }
  };

  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importTeamId, setImportTeamId] = useState("");
  const importRef = React.useRef();

  useEffect(() => { api.get("/teams").then(({ data }) => setTeams(data)).catch(() => {}); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/members/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "members_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const runImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData();
    fd.append("file", file);
    if (importTeamId) fd.append("team_id", importTeamId);
    try {
      const { data } = await api.post("/members/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.created} member(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/members/${editing}`, form); toast.success("Member updated"); setOpen(false); load(); }
      else {
        const { data } = await api.post("/members", form);
        toast.success("Member added");
        setOpen(false); load();
        if (data.temp_password) {
          setNewCredential({ memberId: data.id, name: `${data.first_name} ${data.last_name}`.trim(), email: data.email, phone: data.phone, password: data.temp_password, welcome: data.welcome });
        }
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Could not copy"); }
  };

  const [sending, setSending] = useState("");
  const sendCredentials = async (channel) => {
    if (!newCredential?.memberId) return;
    setSending(channel);
    try {
      const { data } = await api.post(`/members/${newCredential.memberId}/send-credentials`, { channel, password: newCredential.password });
      if (data.status === "simulated") {
        toast.success(`${channel === "email" ? "Email" : "SMS"} queued (simulated — add ${channel === "email" ? "Resend" : "Twilio"} keys to send for real)`);
      } else if (data.status === "failed") {
        toast.error(`Couldn't send by ${channel === "email" ? "email" : "text"} to ${data.target} — check the ${channel === "email" ? "email address" : "phone number"} is valid.`);
      } else {
        toast.success(`Login details sent by ${channel === "email" ? "email" : "text"} to ${data.target}`);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(""); }
  };

  const downloadAccounts = (accounts) => {
    const rows = [["Name", "Email", "Temporary Password"], ...accounts.map((a) => [a.name, a.email, a.temp_password])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `member_logins_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [mergeFor, setMergeFor] = useState(null);   // one member of the pair
  const [mergeOther, setMergeOther] = useState(null); // the chosen duplicate/other
  const [mergeMasterId, setMergeMasterId] = useState(null); // which to keep
  const [mergeSearch, setMergeSearch] = useState("");
  const [merging, setMerging] = useState(false);
  const [lastMerge, setLastMerge] = useState(null); // { snapshot_id, message } — enables 10-min undo
  const [undoing, setUndoing] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const openMerge = (m) => { setMergeFor(m); setMergeOther(null); setMergeMasterId(m.id); setMergeSearch(""); };
  const openMergePair = (a, b) => { setMergeFor(a); setMergeOther(b); setMergeMasterId(a.id); setMergeSearch(""); };
  const openDuplicates = async () => {
    setDupOpen(true); setDupLoading(true);
    try {
      const { data } = await api.get("/members/duplicates");
      setDupGroups(data || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDupLoading(false); }
  };
  const doMerge = async () => {
    if (!mergeFor || !mergeOther) return;
    const masterId = mergeMasterId;
    const dupId = masterId === mergeFor.id ? mergeOther.id : mergeFor.id;
    setMerging(true);
    try {
      const { data } = await api.post(`/members/${masterId}/merge/${dupId}`);
      toast.success(data.message || "Members merged");
      if (data.snapshot_id) {
        setLastMerge({ snapshot_id: data.snapshot_id, message: data.message || "Members merged" });
        setTimeout(() => setLastMerge((cur) => (cur && cur.snapshot_id === data.snapshot_id ? null : cur)), 10 * 60 * 1000);
      }
      setMergeFor(null); setMergeOther(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setMerging(false); }
  };
  const undoMerge = async () => {
    if (!lastMerge) return;
    setUndoing(true);
    try {
      const { data } = await api.post(`/members/merge/${lastMerge.snapshot_id}/undo`);
      toast.success(data.message || "Merge undone");
      setLastMerge(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUndoing(false); }
  };
  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.delete(`/members/${confirmDel.id}`);
      logDeletion("member", confirmDel.name || `${confirmDel.first_name || ""} ${confirmDel.last_name || ""}`.trim() || "Member");
      toast.success("Member removed");
      setConfirmDel(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const resetPwd = async (m) => {
    const name = `${m.first_name} ${m.last_name}`.trim();
    if (!window.confirm(`Set a new temporary password for ${name}? They'll be asked to change it on next sign in.`)) return;
    try {
      const { data } = await api.post(`/members/${m.id}/reset-password`);
      toast.success("Password reset");
      setNewCredential({ memberId: m.id, name: data.name || name, email: data.email, phone: m.phone, password: data.temp_password, reset: true });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [selected, setSelected] = useState([]);
  const [dupIds, setDupIds] = useState(new Set());
  const [dupReason, setDupReason] = useState({});
  const [busyBulk, setBusyBulk] = useState(false);
  const [smsFlagged, setSmsFlagged] = useState([]);
  const loadSmsFlagged = useCallback(() => {
    api.get("/members/sms-flagged").then(({ data }) => setSmsFlagged(data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadSmsFlagged(); }, [loadSmsFlagged]);
  const clearSmsFlag = async (id) => {
    try {
      await api.post(`/members/${id}/clear-sms-flag`);
      setSmsFlagged((arr) => arr.filter((x) => x.id !== id));
      toast.success("Marked as fixed");
    } catch { toast.error("Could not clear the flag"); }
  };
  useEffect(() => {
    api.get("/members/duplicates").then(({ data }) => {
      const s = new Set(); const reasons = {};
      (data || []).forEach((g) => (g.members || g.records || []).forEach((mm) => {
        s.add(mm.id);
        reasons[mm.id] = /phone/i.test(g.reason || "") ? "phone" : "name";
      }));
      setDupIds(s); setDupReason(reasons);
    }).catch(() => {});
  }, []);
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const clearSelection = () => setSelected([]);
  const bulkCareStage = async (care_stage) => {
    setBusyBulk(true);
    try {
      const { data } = await api.patch("/members/bulk/care-stage", { member_ids: selected, care_stage });
      toast.success(`${data.updated} member${data.updated === 1 ? "" : "s"} moved to ${care_stage === "integrated" ? "integrated" : "pastoral care"}`);
      clearSelection(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyBulk(false); }
  };
  const bulkReset = async () => {
    if (!window.confirm(`Send a password reset to ${selected.length} member${selected.length === 1 ? "" : "s"}? Each will be emailed a new temporary password.`)) return;
    setBusyBulk(true);
    try {
      const { data } = await api.post("/members/bulk/reset-password", { member_ids: selected });
      toast.success(`Sent ${data.sent}, skipped ${data.skipped}${data.failed ? `, failed ${data.failed}` : ""}`);
      clearSelection(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyBulk(false); }
  };
  const setCareOne = async (m, care_stage) => {
    try {
      await api.patch("/members/bulk/care-stage", { member_ids: [m.id], care_stage });
      toast.success(care_stage === "integrated" ? "Marked as integrated" : "Moved to pastoral care");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files[0];
    if (!f || !editing) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/members/${editing}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, avatar_file_id: data.avatar_file_id }));
      toast.success("Photo updated"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem("sj_members_page_size")) || 25);
  const [page, setPage] = useState(1);
  const [letter, setLetter] = useState(() => localStorage.getItem("sj_members_letter") || "");
  const [savedViews, setSavedViews] = useState(() => { try { return JSON.parse(localStorage.getItem("sj_members_views") || "[]"); } catch { return []; } });
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const [defaultView, setDefaultView] = useState(() => localStorage.getItem("sj_members_default_view") || "");
  const appliedDefault = React.useRef(false);
  useEffect(() => { localStorage.setItem("sj_members_page_size", String(pageSize)); }, [pageSize]);
  useEffect(() => { localStorage.setItem("sj_members_letter", letter); }, [letter]);
  useEffect(() => { localStorage.setItem("sj_members_views", JSON.stringify(savedViews)); }, [savedViews]);
  useEffect(() => { localStorage.setItem("sj_members_default_view", defaultView); }, [defaultView]);
  useEffect(() => { setPage(1); }, [search, filter, sortKey, sortDir, letter, pageSize, flaggedOnly]);
  const applyView = (v) => { setFilter(v.filter ?? "all"); setLetter(v.letter ?? ""); setSort(v.sortKey ?? "", v.sortDir ?? "asc"); setSearch(v.search ?? ""); };
  useEffect(() => {
    if (appliedDefault.current || !defaultView) return;
    const v = savedViews.find((x) => x.name === defaultView);
    if (v) { applyView(v); appliedDefault.current = true; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const saveView = () => {
    const name = viewName.trim();
    if (!name) return;
    const v = { name, filter, letter, sortKey, sortDir, search };
    setSavedViews((prev) => [...prev.filter((x) => x.name !== name), v]);
    setViewName(""); setSaveViewOpen(false);
    toast.success(`Saved view "${name}"`);
  };
  const deleteView = (name) => { setSavedViews((prev) => prev.filter((x) => x.name !== name)); setDefaultView((d) => (d === name ? "" : d)); };
  const toggleDefaultView = (name) => setDefaultView((cur) => (cur === name ? "" : name));
  const surnameInitial = (m) => (m.last_name || m.first_name || "").trim().charAt(0).toUpperCase();
  const availableLetters = new Set(members.map(surnameInitial).filter(Boolean));
  const sortedAll = sortRows(members, sortKey, sortDir, {
    name: (m) => `${m.first_name || ""} ${m.last_name || ""}`.trim(),
    type: (m) => m.membership_type,
    status: (m) => m.status,
    joined: (m) => m.join_date,
    postcode: (m) => m.postcode,
    last_login: (m) => m.last_login || "",
  });
  const letteredMembers = letter ? sortedAll.filter((m) => surnameInitial(m) === letter) : sortedAll;
  const sortedMembers = flaggedOnly ? letteredMembers.filter((m) => m.sms_flagged) : letteredMembers;
  const totalPages = Math.max(1, Math.ceil(sortedMembers.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pagedMembers = sortedMembers.slice((curPage - 1) * pageSize, curPage * pageSize);
  const pageNumbers = (() => {
    const out = [];
    for (let p = 1; p <= totalPages; p++) {
      if (p === 1 || p === totalPages || (p >= curPage - 1 && p <= curPage + 1)) out.push(p);
      else if (out[out.length - 1] !== "…") out.push("…");
    }
    return out;
  })();


  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Members</h1>
          <p style={{ color: "var(--sj-text-2)" }}>Your church family directory</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <button data-testid="import-members-button" onClick={() => { setImportResult(null); setImportOpen(true); }}
            className="flex items-center gap-2 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-sm font-medium border transition-all hover:bg-[#EDF2F4]"
            style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
            <Upload className="h-4 w-4 sm:h-5 sm:w-5" /> Bulk Import
          </button>
          <button data-testid="export-members-button" onClick={exportCsv}
            className="flex items-center gap-2 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-sm font-medium border transition-all hover:bg-[#EDF2F4]"
            style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
            <Download className="h-4 w-4 sm:h-5 sm:w-5" /> Export CSV
          </button>
          <button data-testid="consent-reminder-button" onClick={sendConsentReminder} disabled={sendingConsent}
            title="Email admins the list of members still showing Pending data-consent"
            className="flex items-center gap-2 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-sm font-medium border transition-all hover:bg-[#EDF2F4] disabled:opacity-60"
            style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
            <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5" /> {sendingConsent ? "Sending…" : "Consent report"}
          </button>
          <button data-testid="review-duplicates-button" onClick={openDuplicates}
            className="flex items-center gap-2 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-sm font-medium border transition-all hover:bg-[#EDF2F4]"
            style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
            <Copy className="h-4 w-4 sm:h-5 sm:w-5" /> Review duplicates
          </button>
          <button data-testid="add-member-button" onClick={openNew}
            className="flex items-center gap-2 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-sm text-white font-medium shadow-sm transition-all hover:brightness-105"
            style={{ background: "var(--sj-primary)" }}>
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" /> Add Member
          </button>
        </div>
      </div>

      {lastMerge && (
        <div data-testid="merge-undo-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3" style={{ borderColor: "#B9E4D6", background: "#E3F1EC" }}>
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--sj-text-2)" }}>
            <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: "var(--sj-primary)" }} />
            <span>{lastMerge.message}. You can undo this merge for the next 10 minutes.</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="undo-merge-btn" onClick={undoMerge} disabled={undoing} className="rounded-full px-4 py-2 text-white text-sm font-semibold disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>{undoing ? "Undoing…" : "Undo merge"}</button>
            <button data-testid="dismiss-undo-btn" onClick={() => setLastMerge(null)} title="Dismiss" className="rounded-full p-2 hover:bg-white" style={{ color: "var(--sj-text-3)" }}><XCircle className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {smsFlagged.length > 0 && (
        <div className="rounded-xl border px-4 py-3.5" data-testid="sms-flagged-banner" style={{ borderColor: "#E9C4C4", background: "#FDF2F2" }}>
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-4 w-4 shrink-0" style={{ color: "#B91C1C" }} />
            <p className="text-sm font-semibold" style={{ color: "#B91C1C" }}>{smsFlagged.length} phone number{smsFlagged.length === 1 ? " needs" : "s need"} attention — recent texts failed to send</p>
            <button data-testid="toggle-flagged-filter" onClick={() => setFlaggedOnly((v) => !v)} className="ml-auto rounded-full px-3 py-1 text-xs font-semibold border shrink-0" style={flaggedOnly ? { background: "#B91C1C", color: "#fff", borderColor: "#B91C1C" } : { background: "#fff", color: "#B91C1C", borderColor: "#E9C4C4" }}>{flaggedOnly ? "Show all members" : "Show only these"}</button>
          </div>
          <div className="space-y-1.5">
            {smsFlagged.map((m) => (
              <div key={m.id} data-testid={`sms-flagged-row-${m.id}`} className="flex items-center justify-between gap-3 flex-wrap rounded-lg bg-white px-3 py-2 border" style={{ borderColor: "#F0D9D9" }}>
                <div className="min-w-0 text-sm">
                  <span className="font-semibold" style={{ color: "var(--sj-text)" }}>{m.name}</span>
                  <span className="mx-1.5" style={{ color: "var(--sj-text-3)" }}>·</span>
                  <span style={{ color: "var(--sj-text-2)" }}>{m.phone || "no number"}</span>
                  <span className="ml-2 text-xs" style={{ color: "#B91C1C" }}>{m.sms_fail_count} failure{m.sms_fail_count === 1 ? "" : "s"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`sms-flag-edit-${m.id}`} onClick={async () => { const full = members.find((x) => x.id === m.id) || (await api.get(`/members/${m.id}`)).data; openEdit(full); }} className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-primary)", color: "var(--sj-primary)" }}>Fix number</button>
                  <button data-testid={`sms-flag-clear-${m.id}`} onClick={() => clearSmsFlag(m.id)} className="rounded-full px-3 py-1.5 text-xs font-semibold text-white hover:brightness-105" style={{ background: "#B91C1C" }}>Mark fixed</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-start gap-2.5 rounded-xl border px-4 py-3" data-testid="members-access-note" style={{ borderColor: "var(--sj-border)", background: "#EDF2F4" }}>
        <Info className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--sj-secondary)" }} />
        <p className="text-xs" style={{ color: "var(--sj-text-2)" }}>
          <strong>Who can manage members?</strong> Admins and Pastors always can. Others get access if they're on the Pastoral, Follow-up, Admin or Finance team — or if an admin grants them the "Manage members" permission directly in <span className="font-medium">Users &amp; Access</span>.
        </p>
      </div>

      {stats && (
        <StatStrip testid="members-stats" cols={6} items={[
          { key: "total", label: "Total members", value: stats.total, icon: Users },
          { key: "new_this_month", label: "New this month", value: stats.new_this_month, icon: UserPlus },
          { key: "converts", label: "New believers", value: stats.converts, icon: Sparkles },
          { key: "active", label: "Active", value: stats.active, icon: CheckCircle2 },
          { key: "inactive", label: "Inactive", value: stats.inactive, icon: XCircle },
          { key: "in_pastoral", label: "In pastoral care", value: stats.in_pastoral, icon: HeartHandshake },
        ]} />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-primary)" }} />
          <input data-testid="member-search-input" placeholder="Search by name, email or phone..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm pl-11 pr-4 py-2.5 text-sm" />
        </div>
        <div className="flex flex-wrap gap-2">
          {[["all", "All"], ["active", "Active"], ["new", "New"], ["inactive", "Inactive"], ["needs_review", "Needs Review"]].map(([s, label]) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
              className="rounded-full px-4 py-2 text-sm font-medium border transition-all"
              style={filter === s
                ? { background: s === "needs_review" ? "var(--sj-primary)" : "var(--sj-secondary)", color: "#fff", borderColor: s === "needs_review" ? "var(--sj-primary)" : "var(--sj-secondary)" }
                : { background: "#fff", color: s === "needs_review" ? "var(--sj-primary)" : "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
              {label}
            </button>
          ))}
          <span className="mx-1 self-center h-6 w-px" style={{ background: "var(--sj-border)" }} />
          {[["", "Everyone"], ["pastoral", "In pastoral care"], ["integrated", "Integrated"]].map(([s, label]) => (
            <button key={s || "any"} data-testid={`care-filter-${s || "any"}`} onClick={() => setCareStage(s)}
              className="rounded-full px-4 py-2 text-sm font-medium border transition-all"
              style={careStage === s
                ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" }
                : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {members.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="members-saved-views">
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Saved views</span>
          {savedViews.length === 0 && <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>None yet — set a status/letter/sort, then save it.</span>}
          {savedViews.map((v) => (
            <span key={v.name} className="inline-flex items-center gap-1 rounded-full pl-1 pr-1 py-1 text-xs font-medium" style={{ background: "#0B5E4E0F", color: "var(--sj-primary)", border: "1px solid #0B5E4E33" }}>
              <button type="button" data-testid={`pin-view-${v.name}`} onClick={() => toggleDefaultView(v.name)}
                title={defaultView === v.name ? "Default view — opens automatically (tap to unpin)" : "Set as default (opens automatically)"}
                className="h-4 w-4 inline-flex items-center justify-center">
                <Star className="h-3.5 w-3.5" style={{ fill: defaultView === v.name ? "currentColor" : "none" }} />
              </button>
              <button type="button" data-testid={`apply-view-${v.name}`} onClick={() => applyView(v)} className="hover:underline px-0.5">{v.name}</button>
              <button type="button" data-testid={`delete-view-${v.name}`} onClick={() => deleteView(v.name)} title="Delete view" className="h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-[#0B5E4E1A]"><XCircle className="h-3.5 w-3.5" /></button>
            </span>
          ))}
          <button type="button" data-testid="save-view-btn" onClick={() => setSaveViewOpen(true)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border transition-colors hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
            <Plus className="h-3.5 w-3.5" /> Save this view
          </button>
        </div>
      )}

      <Dialog open={saveViewOpen} onOpenChange={setSaveViewOpen}>
        <DialogContent data-testid="save-view-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head text-xl">Save this view</DialogTitle></DialogHeader>
          <p className="text-xs -mt-1" style={{ color: "var(--sj-text-3)" }}>
            Captures status <strong style={{ color: "var(--sj-text-2)" }}>{filter}</strong>, letter <strong style={{ color: "var(--sj-text-2)" }}>{letter || "All"}</strong>{search ? <> and search "<strong style={{ color: "var(--sj-text-2)" }}>{search}</strong>"</> : ""}, plus the current sort.
          </p>
          <input data-testid="view-name-input" autoFocus value={viewName} onChange={(e) => setViewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveView(); }}
            placeholder="e.g. Active · Surname A" className={`${inputCls} mt-1`} />
          <DialogFooter>
            <button type="button" onClick={() => setSaveViewOpen(false)} className="rounded-xl px-4 py-2 text-sm font-medium" style={{ border: "1px solid var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="save-view-confirm" onClick={saveView} disabled={!viewName.trim()} className="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {members.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" data-testid="members-az-jump">
          <button type="button" data-testid="az-all" onClick={() => setLetter("")}
            className="min-w-[30px] px-2 py-1 rounded-md text-xs font-semibold transition-colors"
            style={letter === "" ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)", border: "1px solid var(--sj-border)" }}>All</button>
          {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((ch) => {
            const has = availableLetters.has(ch);
            const active = letter === ch;
            return (
              <button key={ch} type="button" data-testid={`az-${ch}`} disabled={!has}
                onClick={() => setLetter(active ? "" : ch)}
                className="min-w-[30px] px-2 py-1 rounded-md text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                style={active ? { background: "var(--sj-primary)", color: "#fff" } : { color: has ? "var(--sj-text-2)" : "var(--sj-text-3)", border: "1px solid var(--sj-border)" }}>
                {ch}
              </button>
            );
          })}
        </div>
      )}


      {selected.length > 0 && (
        <div className={`${CARD} px-5 py-3 flex flex-wrap items-center gap-3`} data-testid="members-bulk-bar" style={{ borderColor: "var(--sj-primary)" }}>
          <span className="text-sm font-semibold" style={{ color: "var(--sj-text)" }} data-testid="bulk-selected-count">{selected.length} selected</span>
          <div className="flex flex-wrap gap-2 ml-auto">
            <button data-testid="bulk-message" disabled={busyBulk} onClick={() => navigate("/messaging", { state: { memberIds: selected } })} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-secondary)" }}><Mail className="h-4 w-4" /> Message selected</button>
            <button data-testid="bulk-integrated" disabled={busyBulk} onClick={() => bulkCareStage("integrated")} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}><HeartHandshake className="h-4 w-4" /> Mark integrated</button>
            <button data-testid="bulk-pastoral" disabled={busyBulk} onClick={() => bulkCareStage("pastoral")} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border hover:bg-[#E3F1EC] disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><HeartHandshake className="h-4 w-4" /> Move to pastoral care</button>
            <button data-testid="bulk-reset-password" disabled={busyBulk} onClick={bulkReset} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border hover:bg-[#E3F1EC] disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><KeyRound className="h-4 w-4" /> Send password reset</button>
            <button data-testid="bulk-clear" onClick={clearSelection} className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium" style={{ color: "var(--sj-text-3)" }}>Clear</button>
          </div>
        </div>
      )}

      <div className={`${CARD} overflow-hidden`} data-testid="members-table">
        {loading ? (
          <div className="p-12 text-center" style={{ color: "var(--sj-text-3)" }}>Loading...</div>
        ) : members.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center gap-3" style={{ color: "var(--sj-text-3)" }}>
            <Users className="h-10 w-10" /> No members found.
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ background: "var(--sj-bg)", color: "var(--sj-text-2)" }}>
                  <th className="py-3.5 pl-5 pr-2 w-10">
                    <input type="checkbox" data-testid="members-select-all" className="h-4 w-4 accent-[#0B5E4E]"
                      checked={pagedMembers.length > 0 && pagedMembers.every((m) => selected.includes(m.id))}
                      onChange={(e) => { const ids = pagedMembers.map((m) => m.id); setSelected((s) => e.target.checked ? [...new Set([...s, ...ids])] : s.filter((x) => !ids.includes(x))); }} />
                  </th>
                  <SortableTh label="Name" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-members-name" className="py-3.5 px-5" />
                  <th className="py-3.5 px-5 font-semibold uppercase tracking-wider text-xs">Contact</th>
                  <SortableTh label="Type" sortKey="type" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-members-type" className="py-3.5 px-5" />
                  <SortableTh label="Status" sortKey="status" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-members-status" className="py-3.5 px-5" />
                  <SortableTh label="Joined" sortKey="joined" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-members-joined" className="py-3.5 px-5 hidden lg:table-cell" />
                  <SortableTh label="Postcode" sortKey="postcode" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-members-postcode" className="py-3.5 px-5 hidden xl:table-cell" />
                  <th className="py-3.5 px-5 font-semibold uppercase tracking-wider text-xs hidden xl:table-cell">Consent</th>
                  <th className="py-3.5 px-5 font-semibold uppercase tracking-wider text-xs text-right sticky right-0 z-10" style={{ background: "var(--sj-bg)", boxShadow: "-8px 0 8px -6px rgba(0,0,0,0.12)" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedMembers.map((m) => {
                  const st = STATUS_STYLE[m.status] || STATUS_STYLE.inactive;
                  return (
                    <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-t hover:bg-[#EDF2F4] transition-colors" style={{ borderColor: "var(--sj-border)" }}>
                      <td className="py-4 pl-5 pr-2">
                        <input type="checkbox" data-testid={`member-select-${m.id}`} className="h-4 w-4 accent-[#0B5E4E]" checked={selected.includes(m.id)} onChange={() => toggleSelect(m.id)} />
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 overflow-hidden" style={{ background: "var(--sj-ochre)" }}>
                            {m.avatar_file_id
                              ? <img src={mediaUrl(m.avatar_file_id)} alt="" className="h-full w-full object-cover" />
                              : <>{(m.first_name || "?")[0]}{(m.last_name || "")[0]}</>}
                          </div>
                          <div>
                            <div className="font-semibold flex items-center gap-2 flex-wrap" style={{ color: "var(--sj-text)" }}>
                              {m.first_name} {m.last_name}
                              {m.needs_review && (
                                <span data-testid={`needs-review-badge-${m.id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#0B5E4E1A", color: "#0B5E4E" }} title="Missing phone or email — please complete">Needs review</span>
                              )}
                              {dupIds.has(m.id) && (
                                <span data-testid={`duplicate-badge-${m.id}`} onClick={() => openMerge(m)} className="cursor-pointer text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "#FBEAD7", color: "#8a5a1a" }} title={`Possible duplicate — matching ${dupReason[m.id] || "details"}. Click to review & merge`}><Copy className="h-3 w-3" /> Possible duplicate · {dupReason[m.id] === "phone" ? "phone" : "name"}</span>
                              )}
                              {m.care_stage === "integrated" && (
                                <span data-testid={`care-badge-${m.id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#17A88A1A", color: "#084438" }}>Integrated</span>
                              )}
                            </div>
                            {m.is_convert && <span className="text-xs" style={{ color: "var(--sj-primary)" }}>New Believer</span>}
                            {cellMap[m.id] && <div><MemberCellBadge info={cellMap[m.id]} id={m.id} /></div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5" style={{ color: "var(--sj-text-2)" }}>
                        {m.email && <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 text-xs hover:underline"><Mail className="h-3.5 w-3.5" />{m.email}</a>}
                        {m.phone && <div className="text-xs mt-1" data-testid={`call-member-${m.id}`}><PhoneActions phone={m.phone} name={`${m.first_name || ""} ${m.last_name || ""}`.trim()} memberId={m.id} /></div>}
                        <DeliveryBadge lm={m.last_message} />
                      </td>
                      <td className="py-4 px-5" style={{ color: "var(--sj-text-2)" }}>{m.membership_type}</td>
                      <td className="py-4 px-5">
                        <span className="rounded-full px-3 py-1 text-xs font-semibold capitalize" style={{ background: st.bg, color: st.color }}>{m.status}</span>
                      </td>
                      <td className="py-4 px-5 hidden lg:table-cell" style={{ color: "var(--sj-text-2)" }}>{ukDate(m.join_date)}</td>
                      <td className="py-4 px-5 hidden xl:table-cell" data-testid={`member-postcode-${m.id}`} style={{ color: "var(--sj-text-2)" }}>{m.postcode || "—"}</td>
                      <td className="py-4 px-5 hidden xl:table-cell" data-testid={`member-consent-${m.id}`}>
                        {m.privacy_consent_at ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--sj-primary)" }} title={`Consented on ${new Date(m.privacy_consent_at).toLocaleString('en-GB')}`}>
                            <ShieldCheck className="h-3.5 w-3.5" />
                            {new Date(m.privacy_consent_at).toLocaleDateString('en-GB', { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>Pending</span>
                        )}
                      </td>
                      <td className="py-4 px-5 sticky right-0 z-10 bg-white" style={{ boxShadow: "-8px 0 8px -6px rgba(0,0,0,0.12)" }}>
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`message-member-${m.id}`} title="Send a message" onClick={() => navigate("/messaging", { state: { memberId: m.id } })}
                            className="p-2 rounded-lg hover:bg-[#E3F1EC] transition-colors outline-none" style={{ color: "var(--sj-primary)" }}><MessageSquare className="h-4 w-4" /></button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`member-menu-${m.id}`} title="Member actions" className="p-2 rounded-lg hover:bg-[#E3F1EC] transition-colors outline-none" style={{ color: "var(--sj-text-2)" }}><MoreVertical className="h-4 w-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem data-testid={`edit-member-${m.id}`} onClick={() => openEdit(m)} className="cursor-pointer gap-2"><Pencil className="h-4 w-4" /> Edit member</DropdownMenuItem>
                              <DropdownMenuItem data-testid={`reset-password-${m.id}`} onClick={() => resetPwd(m)} className="cursor-pointer gap-2"><KeyRound className="h-4 w-4" /> Set new password</DropdownMenuItem>
                              {m.care_stage === "integrated"
                                ? <DropdownMenuItem data-testid={`care-to-pastoral-${m.id}`} onClick={() => setCareOne(m, "pastoral")} className="cursor-pointer gap-2"><HeartHandshake className="h-4 w-4" /> Move to pastoral care</DropdownMenuItem>
                                : <DropdownMenuItem data-testid={`care-to-integrated-${m.id}`} onClick={() => setCareOne(m, "integrated")} className="cursor-pointer gap-2"><HeartHandshake className="h-4 w-4" /> Mark as integrated</DropdownMenuItem>}
                              <DropdownMenuItem data-testid={`merge-member-${m.id}`} onClick={() => openMerge(m)} className="cursor-pointer gap-2"><Copy className="h-4 w-4" /> Merge duplicate…</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem data-testid={`delete-member-${m.id}`} onClick={() => setConfirmDel(m)} className="cursor-pointer gap-2 text-[#B91C1C] focus:text-[#B91C1C]"><Trash2 className="h-4 w-4" /> Delete member</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {sortedMembers.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t" style={{ borderColor: "var(--sj-border)" }} data-testid="members-pagination">
              <div className="flex items-center gap-3 text-xs" style={{ color: "var(--sj-text-3)" }}>
                <span>Showing <strong style={{ color: "var(--sj-text-2)" }}>{(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, sortedMembers.length)}</strong> of {sortedMembers.length}</span>
                <label className="flex items-center gap-1.5">
                  <span>Per page</span>
                  <select data-testid="members-page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    className="bg-white border rounded-lg px-2 py-1 text-xs outline-none focus:border-[#0B5E4E]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                    {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
              {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button type="button" data-testid="members-prev-page" disabled={curPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#EDF2F4]" style={{ border: "1px solid var(--sj-border)", color: "var(--sj-text-2)" }}>Prev</button>
                {pageNumbers.map((p, idx) => p === "…" ? (
                  <span key={`gap-${idx}`} className="px-1.5 text-sm" style={{ color: "var(--sj-text-3)" }}>…</span>
                ) : (
                  <button key={p} type="button" data-testid={`members-page-${p}`} onClick={() => setPage(p)}
                    className="min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold transition-colors"
                    style={p === curPage ? { background: "var(--sj-primary)", color: "#fff" } : { border: "1px solid var(--sj-border)", color: "var(--sj-text-2)" }}>{p}</button>
                ))}
                <button type="button" data-testid="members-next-page" disabled={curPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#EDF2F4]" style={{ border: "1px solid var(--sj-border)", color: "var(--sj-text-2)" }}>Next</button>
              </div>
              )}
            </div>
          )}
          </>
        )}
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>  <AlertDialogContent data-testid="delete-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head text-xl">
              Delete {confirmDel ? `${confirmDel.first_name} ${confirmDel.last_name}`.trim() : "this member"}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>
                This permanently removes the member and everything linked to them:
                <ul className="mt-3 space-y-1.5 list-none">
                  {[
                    "Their portal login (Users & Access)",
                    "Membership of all teams, incl. Pastoral & Follow-Up",
                    "Their pastoral care history (calls, texts, visits)",
                    "Their prayer requests",
                    "Their recorded service attendance",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <Trash2 className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "var(--sj-primary)" }} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 font-medium" style={{ color: "var(--sj-primary)" }}>This can't be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-member">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-member" onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleting} className="bg-[#0B5E4E] hover:bg-[#084438] text-white">
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!mergeFor} onOpenChange={(o) => !o && setMergeFor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">Merge duplicate member</DialogTitle>
          </DialogHeader>
          {mergeFor && (
            <div className="space-y-4">
              <div className="rounded-xl border p-3 text-sm" style={{ borderColor: "var(--sj-border)", background: "#F1F8F5" }}>
                <span style={{ color: "var(--sj-text-3)" }}>Merging:</span> <b>{mergeFor.first_name} {mergeFor.last_name}</b>
                {mergeFor.phone ? <span style={{ color: "var(--sj-text-3)" }}> · {mergeFor.phone}</span> : null}
              </div>

              {!mergeOther ? (
                <div>
                  <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Find the duplicate record</label>
                  <input data-testid="merge-search" autoFocus value={mergeSearch} onChange={(e) => setMergeSearch(e.target.value)} placeholder="Search by name or phone…" className={`${inputCls} mt-1`} />
                  <div className="mt-2 max-h-56 overflow-y-auto border rounded-xl" style={{ borderColor: "var(--sj-border)" }}>
                    {members
                      .filter((x) => x.id !== mergeFor.id)
                      .filter((x) => { const q = mergeSearch.trim().toLowerCase(); if (!q) return false; return `${x.first_name} ${x.last_name}`.toLowerCase().includes(q) || (x.phone || "").toLowerCase().includes(q) || (x.email || "").toLowerCase().includes(q); })
                      .slice(0, 20)
                      .map((x) => (
                        <button key={x.id} data-testid={`merge-pick-${x.id}`} onClick={() => { setMergeOther(x); setMergeMasterId(mergeFor.id); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#F1F8F5] border-b last:border-b-0" style={{ borderColor: "var(--sj-border)" }}>
                          <b>{x.first_name} {x.last_name}</b>
                          <span style={{ color: "var(--sj-text-3)" }}>{x.phone ? ` · ${x.phone}` : ""}{x.email ? ` · ${x.email}` : ""}</span>
                        </button>
                      ))}
                    {mergeSearch.trim() && members.filter((x) => x.id !== mergeFor.id && (`${x.first_name} ${x.last_name}`.toLowerCase().includes(mergeSearch.trim().toLowerCase()) || (x.phone || "").toLowerCase().includes(mergeSearch.trim().toLowerCase()))).length === 0 && (
                      <p className="px-3 py-3 text-sm" style={{ color: "var(--sj-text-3)" }}>No matches.</p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Which record should be kept?</label>
                    <p className="text-xs mb-2" style={{ color: "var(--sj-text-3)" }}>Everything (courses, attendance, submissions, prayers, teams, login) moves onto the kept record; the other is deleted.</p>
                    <div className="space-y-2">
                      {[mergeFor, mergeOther].map((cand) => (
                        <button key={cand.id} data-testid={`merge-keep-${cand.id}`} onClick={() => setMergeMasterId(cand.id)} className="w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm" style={{ borderColor: mergeMasterId === cand.id ? "var(--sj-primary)" : "var(--sj-border)", background: mergeMasterId === cand.id ? "#E3F1EC" : "#fff" }}>
                          <span><b>{cand.first_name} {cand.last_name}</b>{cand.phone ? <span style={{ color: "var(--sj-text-3)" }}> · {cand.phone}</span> : null}{cand.email ? <span style={{ color: "var(--sj-text-3)" }}> · {cand.email}</span> : null}</span>
                          <span className="text-xs font-semibold shrink-0" style={{ color: mergeMasterId === cand.id ? "var(--sj-primary)" : "var(--sj-text-3)" }}>{mergeMasterId === cand.id ? "KEEP" : "Keep this"}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => setMergeOther(null)} className="text-sm font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>← Pick a different duplicate</button>
                </>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setMergeFor(null)} className="rounded-full px-5 py-2.5 border font-medium text-sm" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
                <button data-testid="confirm-merge-btn" onClick={doMerge} disabled={!mergeOther || merging} className="rounded-full px-6 py-2.5 text-white font-semibold text-sm disabled:opacity-40" style={{ background: "#B91C1C" }}>{merging ? "Merging…" : "Merge & delete duplicate"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">Possible duplicate members</DialogTitle>
          </DialogHeader>
          {dupLoading ? (
            <p className="text-sm py-8 text-center" data-testid="dup-loading" style={{ color: "var(--sj-text-3)" }}>Scanning your directory…</p>
          ) : dupGroups.length === 0 ? (
            <div className="py-8 text-center" data-testid="no-duplicates">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--sj-primary)" }} />
              <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>No likely duplicates found. Your directory looks clean.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>We found {dupGroups.length} group{dupGroups.length > 1 ? "s" : ""} of records that look like the same person (matching phone number or name). Review each and merge if they’re the same.</p>
              {dupGroups.map((g, gi) => (
                <div key={gi} data-testid={`dup-group-${gi}`} className="rounded-xl border p-3" style={{ borderColor: "var(--sj-border)" }}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FCEBDD", color: "#B45309" }}>{g.reason}</span>
                    <span className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>{g.value}</span>
                  </div>
                  <div className="space-y-1.5">
                    {g.members.map((m) => (
                      <div key={m.id} className="text-sm">
                        <b>{m.first_name} {m.last_name}</b>
                        {m.phone ? <span style={{ color: "var(--sj-text-3)" }}> · {m.phone}</span> : null}
                        {m.email ? <span style={{ color: "var(--sj-text-3)" }}> · {m.email}</span> : null}
                      </div>
                    ))}
                  </div>
                  {g.members.length === 2 ? (
                    <div className="mt-3 flex justify-end">
                      <button data-testid={`dup-merge-${gi}`} onClick={() => { setDupOpen(false); openMergePair(g.members[0], g.members[1]); }} className="rounded-full px-4 py-2 text-white text-sm font-semibold" style={{ background: "var(--sj-primary)" }}>Review &amp; merge</button>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs" style={{ color: "var(--sj-text-3)" }}>{g.members.length} records share this. Use the ⋯ menu on a member row to merge them two at a time.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">{editing ? "Edit Member" : "Add Member"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            {editing && (
              <div className="sm:col-span-2 flex items-center gap-4">
                <div className="h-16 w-16 rounded-full overflow-hidden flex items-center justify-center text-white text-lg font-semibold shrink-0" style={{ background: "var(--sj-ochre)" }}>
                  {form.avatar_file_id ? <img src={mediaUrl(form.avatar_file_id)} alt="" className="h-full w-full object-cover" /> : <>{(form.first_name || "?")[0]}{(form.last_name || "")[0]}</>}
                </div>
                <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="member-avatar-input" />
                <button type="button" data-testid="member-avatar-button" onClick={() => avatarRef.current?.click()} className="rounded-full px-4 py-2 text-sm font-medium border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>Upload photo</button>
              </div>
            )}
            {editing && <MemberCellCard info={cellMap[editing]} />}
            <Field label="First name"><input data-testid="form-first-name" required value={form.first_name} onChange={upd("first_name")} className={inputCls} /></Field>
            <Field label="Last name"><input data-testid="form-last-name" required value={form.last_name} onChange={upd("last_name")} className={inputCls} /></Field>
            <Field label="Email"><input data-testid="form-email" type="email" value={form.email} onChange={upd("email")} className={inputCls} /></Field>
            <Field label="Phone"><input data-testid="form-phone" value={form.phone} onChange={upd("phone")} className={inputCls} /></Field>
            <Field label="Gender">
              <select data-testid="form-gender" value={form.gender} onChange={upd("gender")} className={inputCls}>
                <option value="">Select</option><option>Male</option><option>Female</option>
              </select>
            </Field>
            <Field label="Membership type">
              <select data-testid="form-type" value={form.membership_type} onChange={upd("membership_type")} className={inputCls}>
                <option>Member</option><option>Worker</option><option>First Timer</option><option>Leader</option>
              </select>
            </Field>
            <Field label="Date of birth"><input data-testid="form-dob" type="date" value={form.date_of_birth} onChange={upd("date_of_birth")} className={inputCls} /></Field>
            <Field label="Join date"><input data-testid="form-join" type="date" value={form.join_date} onChange={upd("join_date")} className={inputCls} /></Field>
            <Field label="Status">
              <select data-testid="form-status" value={form.status} onChange={upd("status")} className={inputCls}>
                <option value="active">Active</option><option value="new">New</option><option value="inactive">Inactive</option>
              </select>
            </Field>
            <Field label="Follow-up status">
              <select data-testid="form-followup" value={form.follow_up_status} onChange={upd("follow_up_status")} className={inputCls}>
                <option value="none">None</option><option value="pending">Pending</option><option value="contacted">Contacted</option><option value="integrated">Integrated</option>
              </select>
            </Field>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm" style={{ color: "var(--sj-text-2)" }}>
                <input data-testid="form-convert" type="checkbox" checked={form.is_convert} onChange={(e) => setForm({ ...form, is_convert: e.target.checked, convert_date: e.target.checked ? (form.convert_date || new Date().toISOString().slice(0, 10)) : "" })} className="h-4 w-4 accent-[#0B5E4E]" />
                Mark as new believer / convert
              </label>
            </div>
            <div className="sm:col-span-2">
              <Field label="Address"><input data-testid="form-address" value={form.address} onChange={upd("address")} className={inputCls} /></Field>
            </div>
            <Field label="Postcode"><input data-testid="form-postcode" value={form.postcode} onChange={upd("postcode")} className={inputCls} /></Field>
            <div className="sm:col-span-2">
              <Field label="Notes"><textarea data-testid="form-notes" rows={2} value={form.notes} onChange={upd("notes")} className={inputCls} /></Field>
              {editing && <MemberSelfReported m={form} />}
            </div>
            <div className="sm:col-span-2">
              <Field label="Prayer Request"><textarea data-testid="form-prayer-request" rows={2} placeholder="Any prayer request for this member..." value={form.prayer_request} onChange={upd("prayer_request")} className={inputCls} /></Field>
            </div>
            {editing && memberships && (
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="member-memberships">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2" style={{ color: "var(--sj-text-3)" }}>
                    <Users className="h-3.5 w-3.5" /> Teams ({memberships.teams.length})
                  </label>
                  <div className="rounded-xl border p-2 min-h-[44px] flex flex-wrap gap-1.5" style={{ borderColor: "var(--sj-border)" }} data-testid="member-teams">
                    {memberships.teams.length === 0 ? (
                      <span className="text-sm px-1 py-0.5" style={{ color: "var(--sj-text-3)" }}>Not in any team</span>
                    ) : memberships.teams.map((t) => (
                      <span key={t.id} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: t.role === "Leader" ? "#17A88A1A" : "#EDF2F4", color: t.role === "Leader" ? "#0B5E4E" : "var(--sj-text-2)" }}>
                        {t.name}{t.role === "Leader" && <span className="text-[10px] font-bold uppercase">· Lead</span>}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2" style={{ color: "var(--sj-text-3)" }}>
                    <GraduationCap className="h-3.5 w-3.5" /> Courses ({memberships.courses.length})
                  </label>
                  <div className="rounded-xl border p-2 min-h-[44px] space-y-1" style={{ borderColor: "var(--sj-border)" }} data-testid="member-courses">
                    {memberships.courses.length === 0 ? (
                      <span className="text-sm px-1 py-0.5 block" style={{ color: "var(--sj-text-3)" }}>Not enrolled in any course</span>
                    ) : memberships.courses.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-2 text-sm px-1">
                        <span className="min-w-0 truncate font-medium" style={{ color: "var(--sj-text)" }}>{c.title}{c.cohort_name ? <span className="font-normal" style={{ color: "var(--sj-text-3)" }}> · {c.cohort_name}</span> : null}</span>
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0" style={c.status === "approved" ? { background: "#17A88A1A", color: "#0B5E4E" } : { background: "#B8860B1A", color: "#8a5a1a" }}>{c.status || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {editing && callHistory && callHistory.length > 0 && (
              <div className="sm:col-span-2" data-testid="member-call-history">
                <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2" style={{ color: "var(--sj-text-3)" }}>
                  <PhoneCall className="h-3.5 w-3.5" /> Call history ({callHistory.length})
                </label>
                <div className="rounded-xl border divide-y max-h-40 overflow-y-auto" style={{ borderColor: "var(--sj-border)" }}>
                  {callHistory.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" style={{ borderColor: "var(--sj-border)" }}>
                      <span style={{ color: "var(--sj-text)" }}>{c.by}</span>
                      <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>{c.at ? ukDate(c.at) : ""}{c.status && c.status !== "initiated" ? ` · ${c.status}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {editing && attendance && (
              <div className="sm:col-span-2" data-testid="attendance-history">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: "var(--sj-text-3)" }}>
                    <CalendarCheck className="h-3.5 w-3.5" /> Attendance History
                  </label>
                  <span className="text-xs font-semibold" style={{ color: "var(--sj-secondary)" }}>{attendance.attended}/{attendance.total} services · {attendance.rate}%</span>
                </div>
                <div className="rounded-xl border max-h-40 overflow-y-auto" style={{ borderColor: "var(--sj-border)" }}>
                  {attendance.history.length === 0 ? (
                    <div className="p-4 text-sm text-center" style={{ color: "var(--sj-text-3)" }}>No services recorded yet.</div>
                  ) : attendance.history.slice(0, 20).map((h) => (
                    <div key={h.id} className="flex items-center justify-between px-4 py-2 border-b last:border-b-0 text-sm" style={{ borderColor: "var(--sj-border)" }}>
                      <div>
                        <span className="font-medium">{h.title || h.service_type}</span>
                        <span className="ml-2 text-xs" style={{ color: "var(--sj-text-3)" }}>{ukDate(h.service_date)}</span>
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={h.attended ? { background: "#17A88A1A", color: "#17A88A" } : { background: "#56626A1A", color: "#56626A" }}>
                        {h.attended ? "Present" : "Absent"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {editing && (form.care_history?.length > 0) && (
              <div className="sm:col-span-2" data-testid="care-history">
                <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2" style={{ color: "var(--sj-text-3)" }}>
                  <HeartHandshake className="h-3.5 w-3.5" /> Care History
                </label>
                <div className="rounded-xl border max-h-32 overflow-y-auto" style={{ borderColor: "var(--sj-border)" }}>
                  {[...form.care_history].reverse().slice(0, 20).map((h, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2 border-b last:border-b-0 text-sm" style={{ borderColor: "var(--sj-border)" }}>
                      <span className="font-medium">Moved to {h.stage === "integrated" ? "Integrated" : "Pastoral care"}</span>
                      <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>{h.at ? new Date(h.at).toLocaleDateString('en-GB', { day: "numeric", month: "short", year: "numeric" }) : ""}{h.by ? ` · ${h.by}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter className="sm:col-span-2 mt-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" data-testid="save-member-button" className="rounded-full px-6 py-2.5 text-white font-semibold shadow-sm hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>{editing ? "Save changes" : "Add member"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">Bulk import members</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Upload a CSV file. Columns: First Name, Last Name, Email, Phone, Gender, Membership Type, Status, Join Date, Date Of Birth, Address, Notes, Prayer Request, Pastoral Call 1, Pastoral Call 2, Pastoral Call 3. Any prayer request is added to the Prayer Requests queue. Each pastoral call is logged in the care history — use the format <span className="font-semibold">Date | Outcome | Note</span> (e.g. <span className="font-mono text-xs">2026-01-15 | reached | Will visit Sunday</span>) so the real call date is recorded. Rows with an email that already exists are skipped.</p>
            <button data-testid="download-template-button" onClick={downloadTemplate}
              className="flex items-center gap-2 text-sm font-medium rounded-full px-4 py-2 border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
              <Download className="h-4 w-4" /> Download CSV template
            </button>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Assign whole batch to a team (optional)</label>
              <select data-testid="import-team-select" value={importTeamId} onChange={(e) => setImportTeamId(e.target.value)}
                className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm">
                <option value="">No team — just add to directory</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <p className="text-xs mt-1.5" style={{ color: "var(--sj-text-3)" }}>Tip: choose Follow-up to drop new contacts straight into that team's care queue.</p>
            </div>
            <input ref={importRef} data-testid="import-file-input" type="file" accept=".csv,text/csv" onChange={runImport} className="hidden" />
            <button data-testid="choose-import-file-button" disabled={importing} onClick={() => importRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed py-10 transition-all hover:bg-[#EDF2F4] disabled:opacity-60"
              style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
              <FileUp className="h-8 w-8" style={{ color: "var(--sj-primary)" }} />
              <span className="font-medium">{importing ? "Importing…" : "Click to choose a CSV file"}</span>
            </button>
            {importResult && (
              <div className="rounded-xl p-4 text-sm" data-testid="import-result" style={{ background: "var(--sj-bg)" }}>
                <div className="font-semibold" style={{ color: "var(--sj-secondary)" }}>✓ {importResult.created} added{importResult.team ? ` to ${importResult.team}` : ""} · {importResult.skipped} skipped{importResult.flagged ? ` · ${importResult.flagged} need review` : ""}</div>
                {importResult.flagged > 0 && (
                  <div className="text-xs mt-1" style={{ color: "var(--sj-primary)" }}>Tip: use the "Needs Review" filter to complete records missing a phone or email.</div>
                )}
                {importResult.accounts?.length > 0 && (
                  <div className="mt-3">
                    <button data-testid="download-logins-button" onClick={() => downloadAccounts(importResult.accounts)}
                      className="flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 text-white hover:brightness-105" style={{ background: "var(--sj-secondary)" }}>
                      <KeyRound className="h-4 w-4" /> Download {importResult.accounts.length} login detail(s) (CSV)
                    </button>
                    <p className="text-xs mt-1.5" style={{ color: "var(--sj-text-3)" }}>Each member with an email got a temporary password — share it so they can sign in and set their own.</p>
                  </div>
                )}
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 max-h-40 overflow-y-auto space-y-1" style={{ color: "var(--sj-text-3)" }}>
                    {importResult.errors.map((er, i) => <li key={`import-err-${i}-${er}`} className="text-xs">• {er}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setImportOpen(false)} className="rounded-full px-6 py-2.5 text-white font-semibold" style={{ background: "var(--sj-primary)" }}>Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newCredential} onOpenChange={(o) => !o && setNewCredential(null)}>
        <DialogContent data-testid="new-credential-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "#17A88A1A" }}>
                <KeyRound className="h-6 w-6" style={{ color: "var(--sj-secondary)" }} />
              </div>
              <DialogTitle className="font-head text-2xl">{newCredential?.reset ? "Password reset" : "Login created"}</DialogTitle>
            </div>
          </DialogHeader>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>
            {newCredential?.reset
              ? <>A new temporary password was set for <span className="font-semibold">{newCredential?.name}</span>. Share these details — they'll be asked to set their own password on next sign in.</>
              : <>A portal account was created for <span className="font-semibold">{newCredential?.name}</span>. Share these details — they'll be asked to set their own password on first sign in.</>}
          </p>
          {newCredential?.welcome && (newCredential.welcome.email || newCredential.welcome.sms) && (
            <div data-testid="welcome-sent-note" className="mt-2 text-xs font-medium rounded-lg px-3 py-2" style={{ background: "#17A88A12", color: "var(--sj-secondary)" }}>
              A welcome message with these details was automatically {
                [newCredential.welcome.email && `emailed${newCredential.welcome.email === "simulated" ? " (simulated)" : ""}`,
                 newCredential.welcome.sms && `texted${newCredential.welcome.sms === "simulated" ? " (simulated)" : ""}`]
                  .filter(Boolean).join(" and ")
              } to the member.
            </div>
          )}
          <div className="mt-3 rounded-xl border p-4 space-y-2" style={{ borderColor: "var(--sj-border)", background: "var(--sj-bg)" }}>
            <div className="flex items-center justify-between gap-3">
              <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Email</div><div className="font-medium text-sm" data-testid="cred-email">{newCredential?.email}</div></div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Temporary password</div><div className="font-mono font-semibold text-sm" data-testid="cred-password">{newCredential?.password}</div></div>
              <button data-testid="copy-credential-button" onClick={() => copyText(`Email: ${newCredential?.email}\nTemporary password: ${newCredential?.password}`)}
                className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-2 border hover:bg-white" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="email-credentials-button" disabled={!newCredential?.email || sending === "email"} onClick={() => sendCredentials("email")}
                className="flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2.5 border transition-colors hover:bg-white disabled:opacity-50" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
                <Mail className="h-4 w-4" /> {sending === "email" ? "Sending..." : "Email to member"}
              </button>
              <button type="button" data-testid="sms-credentials-button" disabled={!newCredential?.phone || sending === "sms"} onClick={() => sendCredentials("sms")}
                className="flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2.5 border transition-colors hover:bg-white disabled:opacity-50" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
                <Phone className="h-4 w-4" /> {sending === "sms" ? "Sending..." : "Text to member"}
              </button>
            </div>
            <button type="button" onClick={() => setNewCredential(null)} className="rounded-full px-6 py-2.5 text-white font-semibold" style={{ background: "var(--sj-primary)" }}>Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>{label}</label>
      {children}
    </div>
  );
}
